import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Copy, Check, Share2, Mail } from "lucide-react";
import { toast } from "sonner";


import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { getMyReferralOverview } from "@/lib/referrals.functions";

async function authHeaders() {
  const { data } = await supabase.auth.getSession();
  if (!data.session) throw new Error("未ログインです");
  return { Authorization: `Bearer ${data.session.access_token}` };
}

export const Route = createFileRoute("/_authenticated/referral")({
  head: () => ({
    meta: [
      { title: "友達紹介 | KAGEM" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ReferralPage,
});

const SITE_URL =
  (import.meta.env.VITE_SITE_URL as string | undefined)?.replace(/\/$/, "") ||
  (typeof window !== "undefined" ? window.location.origin : "");

function buildShareLink(code: string) {
  return `${SITE_URL}/?ref=${encodeURIComponent(code)}`;
}

function ReferralPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["referral-overview"],
    queryFn: async () => {
      const headers = await authHeaders();
      return getMyReferralOverview({ headers });
    },
  });

  const code = data?.referralCode ?? "";
  const shareLink = code ? buildShareLink(code) : "";

  return (
    <main className="min-h-screen bg-background px-4 py-8">
      <div className="mx-auto max-w-4xl space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">友達紹介</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            友達を招待して、お互いに50クレジットをゲットしよう。
          </p>
        </div>

        {isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : (
          <>
            <InviteCodeCard code={code} shareLink={shareLink} />
            <ShareButtons code={code} shareLink={shareLink} />
            <StatsCards stats={data?.stats} />
            <HistoryList history={data?.history ?? []} />
            <NoticeCard />
          </>
        )}
      </div>
    </main>
  );
}

function InviteCodeCard({ code, shareLink }: { code: string; shareLink: string }) {
  const [copiedCode, setCopiedCode] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  const copy = async (text: string, setter: (v: boolean) => void) => {
    try {
      await navigator.clipboard.writeText(text);
      setter(true);
      toast.success("コピーしました");
      setTimeout(() => setter(false), 2000);
    } catch {
      toast.error("コピーに失敗しました");
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">あなたの招待コード</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-lg border-2 border-dashed border-primary/40 bg-primary/5 p-6 text-center">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">招待コード</p>
          <p className="mt-2 font-mono text-3xl font-bold tracking-widest text-primary">
            {code || "----"}
          </p>
        </div>

        <div className="space-y-2">
          <label className="text-xs font-medium text-muted-foreground">招待リンク</label>
          <div className="flex gap-2">
            <input
              readOnly
              value={shareLink}
              className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
              onFocus={(e) => e.currentTarget.select()}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => copy(shareLink, setCopiedLink)}
              disabled={!shareLink}
            >
              {copiedLink ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              <span className="ml-1.5 hidden sm:inline">リンクをコピー</span>
            </Button>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => copy(code, setCopiedCode)}
            disabled={!code}
            className="text-xs"
          >
            {copiedCode ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
            <span className="ml-1">コードのみをコピー</span>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ShareButtons({ code, shareLink }: { code: string; shareLink: string }) {
  const [canNativeShare, setCanNativeShare] = useState(false);

  useEffect(() => {
    setCanNativeShare(typeof navigator !== "undefined" && !!navigator.share);
  }, []);

  if (!shareLink) return null;

  const shareText = `変身動画作成サービス『KAGEM』を使ってみない？私の招待コード（${code}）で50クレジットもらえるよ\n${shareLink}`;
  const xUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`;
  const lineUrl = `https://line.me/R/msg/text/?${encodeURIComponent(shareText)}`;
  const mailUrl = `mailto:?subject=${encodeURIComponent("KAGEMに招待します")}&body=${encodeURIComponent(shareText)}`;

  const handleNativeShare = async () => {
    try {
      await navigator.share({
        title: "KAGEM",
        text: shareText,
        url: shareLink,
      });
    } catch {
      // user cancelled
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">シェアする</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-2">
          <Button asChild variant="outline" size="sm">
            <a href={xUrl} target="_blank" rel="noreferrer noopener">
              <span className="mr-1 font-bold">𝕏</span>
              Xでシェア
            </a>
          </Button>
          <Button asChild variant="outline" size="sm">
            <a href={lineUrl} target="_blank" rel="noreferrer noopener">
              LINEでシェア
            </a>
          </Button>
          <Button asChild variant="outline" size="sm">
            <a href={mailUrl}>
              <Mail className="h-4 w-4" />
              <span className="ml-1.5">メールで送る</span>
            </a>
          </Button>
          {canNativeShare && (
            <Button variant="outline" size="sm" onClick={handleNativeShare}>
              <Share2 className="h-4 w-4" />
              <span className="ml-1.5">その他で共有</span>
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function StatsCards({
  stats,
}: {
  stats?: { invitedCount: number; claimedCount: number; pendingCount: number; ticketsEarned: number };
}) {
  const items = [
    { label: "招待した友達", value: stats?.invitedCount ?? 0, suffix: "人" },
    { label: "獲得クレジット", value: stats?.ticketsEarned ?? 0, suffix: "" },
    { label: "招待中（未確定）", value: stats?.pendingCount ?? 0, suffix: "人" },
  ];

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      {items.map((item) => (
        <Card key={item.label}>
          <CardContent className="pt-6">
            <p className="text-xs text-muted-foreground">{item.label}</p>
            <p className="mt-1 text-3xl font-bold text-foreground">
              {item.value}
              <span className="ml-1 text-sm font-medium text-muted-foreground">{item.suffix}</span>
            </p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

type HistoryItem = {
  id: string;
  createdAt: string;
  referredDisplayName: string;
  status: "claimed" | "pending" | "review";
  rewardCredits: number;
  claimedAt: string | null;
};

function HistoryList({ history }: { history: HistoryItem[] }) {
  if (history.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">招待履歴</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="py-8 text-center text-sm text-muted-foreground">
            まだ招待履歴がありません。リンクをシェアして友達を招待しましょう！
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">招待履歴</CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="divide-y divide-border">
          {history.map((item) => (
            <li key={item.id} className="flex items-center justify-between py-3">
              <div>
                <p className="text-sm font-medium">{item.referredDisplayName}</p>
                <p className="text-xs text-muted-foreground">
                  {new Date(item.createdAt).toLocaleDateString("ja-JP")}
                </p>
              </div>
              <div className="flex items-center gap-3">
                {item.status === "claimed" ? (
                  <>
                    <Badge variant="default">付与済</Badge>
                    <span className="text-sm font-semibold text-emerald-600">
                      +{item.rewardCredits}枚
                    </span>
                  </>
                ) : item.status === "review" ? (
                  <Badge variant="outline">確認中</Badge>
                ) : (
                  <Badge variant="secondary">課金待ち</Badge>
                )}
              </div>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function NoticeCard() {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">注意事項</CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="space-y-1.5 text-xs text-muted-foreground">
          <li>
            ・友達が初めて <strong>Standard プラン以上</strong> に加入したタイミングで、紹介者・被紹介者の両方に50クレジットを進呈します。
          </li>
          <li>・お試しクレジットや追加パック、Light プランへの加入ではボーナスは付与されません。</li>
          <li>・招待コードは1人につき1度しか有効化できません。</li>
          <li>・自己紹介・重複登録など不正と判断された紹介は無効になります。</li>
        </ul>
      </CardContent>
    </Card>
  );
}
